import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useSwipeable } from 'react-swipeable';

import PropTypes from 'prop-types';

import { useApp } from 'hooks/selectors';

import PromoPopupPortal from './PromoPopupPortal';
import { getoutAnimation, Overlay, PopupBox, PopupBoxInner, PopupOuter } from './styles';

const PromoPopup = React.forwardRef(function PromoPopup(props, innerRef) {
    const { isShowSpinner, isShowModal } = useApp();
    const overlayRef = useRef(null);
    const [isOpen, setOpen] = useState(false);
    const [state, setState] = useState({
        isShow: false,
        animationEnd: false,
    });
    const [data, setData] = useState(null);
    const { as: Wrapper = PromoPopupPortal } = props;

    useImperativeHandle(
        innerRef,
        () => ({
            open: (data = null) => {
                setOpen(true);
                if (data) {
                    setData(data);
                }
            },
            close: () => setOpen(false),
        }),
        [],
    );

    useEffect(() => {
        if (__isBrowser__ && isOpen) {
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', keyPressHandler);
        }
        return () => {
            if (__isBrowser__ && isOpen && !isShowModal && !props.overflowHidden) {
                document.body.style.overflow = 'visible';
                document.removeEventListener('keydown', keyPressHandler);
            }
        };
    }, [isShowModal, isOpen]);

    useEffect(() => {
        if (isOpen) {
            setState((prevState) => {
                return {
                    ...prevState,
                    isShow: true,
                };
            });
        } else if (!isOpen && state.isShow) {
            setState((prevState) => {
                return {
                    ...prevState,
                    animationEnd: true,
                };
            });
        }
    }, [isOpen]);

    const slide = () => {
        setOpen(false);
    };

    const handlers = useSwipeable({
        onSwipedDown: () => slide(),
        delta: 20,
        preventDefaultTouchmoveEvent: true,
        trackTouch: true,
    });

    const keyPressHandler = (e) => {
        if (e.keyCode === 27) {
            e.preventDefault();
            setOpen(false);
        }
    };

    const overlayClick = (e) => {
        if (overlayRef.current && !overlayRef.current.contains(e.target)) {
            e.preventDefault();
            setOpen(false);
        }
    };

    const closeAnimationPopup = useCallback((e) => {
        if (e?.animationName === getoutAnimation?.name) {
            flushSync(() => {
                setState({
                    isShow: false,
                    animationEnd: false,
                });
                setData(null);
            });
        }
    }, []);

    const animated = useMemo(() => !isOpen && state.animationEnd, [isOpen, state.animationEnd]);

    if (state.isShow) {
        return (
            <Wrapper>
                <div id="promo-popup" {...handlers}>
                    <Overlay onClick={overlayClick}>
                        <PopupOuter>
                            <PopupBox animated={animated} onAnimationEnd={closeAnimationPopup} isWidePopup={props.isWidePopup}>
                                <PopupBoxInner ref={overlayRef} isShowSpinner={isShowSpinner}>
                                    {data}
                                </PopupBoxInner>
                            </PopupBox>
                        </PopupOuter>
                    </Overlay>
                </div>
            </Wrapper>
        );
    } else {
        return null;
    }
});
PromoPopup.propTypes = {
    isWidePopup: PropTypes.bool,
    as: PropTypes.func,
    overflowHidden: PropTypes.bool,
};
export default PromoPopup;
